import axios from "axios";
import FinTaxMasterSelect from "components/SelectComponents/FinTaxMasterSelect";
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import * as Yup from "yup";

Modal.setAppElement("#root");

function genUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  // fallback
  return "uuid-" + Math.random().toString(36).slice(2, 11);
}

export default function FinCompanyPurchaseOrderInvoiceAddModal() {
  const [addModalOpen, setAddModalOpen] = useState(true);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [allTaxMasters, setAllTaxMasters] = useState([]);
  const [toGLAccount, setToGLAccount] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [searchInput, setSearchInput] = useState(null);
  const [searchItemInput, setsearchItemInput] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [tdsPercent, setTdsPercent] = useState(0);
  const token = sessionStorage.getItem("token");
  const headers = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const navigate = useNavigate();

  // fetch suppliers when searchInput changes
  useEffect(() => {
    SupplierGet();
  }, [searchInput]);
  // fetch ledgers when searchInput changes
  useEffect(() => {
    fetchLedgers();
    fetchAllTaxMasters();
  }, []);

  const SupplierGet = () => {
    axios
      .post(
        "/api/purple-fusion/supplier/get",
        { name: searchInput || "" },
        headers
      )
      .then((response) => {
        const suppliers = response.data?.map((supplier) => ({
          label: supplier.name,
          value: supplier.id,
          fin_payment_term_code: supplier.payment_term_code,
          fin_tax_master_tds_id: supplier.fin_tax_master_tds_id,
        }));
        setSupplierOptions(suppliers || []);
      })
      .catch((err) => {
        console.error("Supplier fetch failed:", err);
      });
  };

  const fetchLedgers = () => {
    axios
      .post("/api/purple-fusion-financial/ledger/get", {}, headers)
      .then((response) => {
        console.log(response.data);

        const ledger = response.data?.map((ledger) => ({
          label: ledger.name + " (" + ledger.code + ")",
          value: ledger.id,
          fin_payment_term_code: ledger.payment_term_code,
          fin_tax_master_tds_id: ledger.fin_tax_master_tds_id,
        }));
        setToGLAccount(ledger);
      })
      .catch((error) => {
        console.error("Error fetching ledgers:", error);
      });
    // setLoading(false);
  };

  const getTaxMasterDetails = (taxMasterId) => {
    axios
      .post(
        "/api/purple-fusion-financial/fin-tax-master/get",
        { id: taxMasterId },
        headers
      )
      .then((res) => {
        if (res.data) {
          const percent = res?.data[0]?.percentage_rate || 0;
          setTdsPercent(percent);
        }
      })
      .catch((err) => {
        console.log("TDS fetch error:", err);
      });
  };

  // fetch items when supplier or item search changes
  useEffect(() => {
    getitemList();
  }, [searchItemInput, selectedSupplierId]);

  const getitemList = () => {
    axios
      .post(
        "/api/purple-fusion/itemsupplier/getforsearch-gst",
        { supplier_id: selectedSupplierId, search_parameter: searchItemInput },
        headers
      )
      .then((res) => {
        // normalize to expected fields if needed
        setItemOptions(res.data || []);
      })
      .catch((err) => {
        console.error("Item list fetch failed:", err);
      });
  };

  const fetchAllTaxMasters = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const headers = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.post(
        "/api/purple-fusion-financial/fin-tax-master/get",
        {
          tax_type_id: 2,
          is_active: true,
        }, // fetch all active
        headers
      );
      // console.log(response.data);

      const tds = response.data?.map((tax) => ({
        value: tax.id,
        label: `${tax.code} - ${tax.name}`,
        taxData: tax,
      }));

      setAllTaxMasters(tds || []);
    } catch (err) {
      console.error("Error fetching all tax masters:", err);
    } finally {
    }
  };

  const createManualInvoice = async (payload) => {
    try {
      const response = await axios.post(
        "/api/purple-fusion-financial/fin-cpo-invoice/create-manual",
        payload,
        headers
      );

      const data = response.data;

      // ----- SUCCESS TOAST -----
      if (data?.status) {
        toast.success(data?.message || "Invoice created successfully!");
      } else {
        toast.error(data?.message || "Failed to create invoice!");
      }

      return data;
    } catch (error) {
      console.error("API Error:", error);
      // ----- ERROR TOAST -----
      toast.error(
        error.response?.data?.message || "Server error! Please try again."
      );

      throw error;
    }
  };

  const validationSchema = Yup.object().shape({
    supplier_id: Yup.number().required("Supplier is required"),
    to_gl_account: Yup.number().required("GL is required"),
    invoice_number: Yup.string().required("Invoice number is required"),
    invoice_date: Yup.date().required("Invoice date is required"),
    posting_date: Yup.date().required("Posting date is required"),
    total_invoice_amount: Yup.number()
      .min(0, "Must be positive")
      .required("Total invoice amount required"),
    items: Yup.array().of(
      Yup.object().shape({
        item_name: Yup.string().required("Item name required"),
        quantity: Yup.number()
          .min(1, "Must be > 0")
          .required("Quantity required"),
        cost_price: Yup.number()
          .min(0, "Must be ≥ 0")
          .required("Price required"),
        discount_percentage: Yup.number().min(0).max(100),
      })
    ),
  });

  // pure calc helper (input: single item object)
  const computeItemValues = (item) => {
    const qty = Number(item.quantity) || 0;
    const cost = Number(item.cost_price) || 0;
    const discPerc = Number(item.discount_percentage) || 0;

    const gross = qty * cost;
    const discount_amount = +(gross * discPerc) / 100;
    const taxable = +(gross - discount_amount);

    const cgst_percent = Number(item.cgst_percent) || 0;
    const sgst_percent = Number(item.sgst_percent) || 0;
    const igst_percent = Number(item.igst_percent) || 0;

    const cgst_amount = +(taxable * cgst_percent) / 100;
    const sgst_amount = +(taxable * sgst_percent) / 100;
    const igst_amount = +(taxable * igst_percent) / 100;

    const total_amount = +(taxable + cgst_amount + sgst_amount + igst_amount);

    return {
      discount_amount: Number(discount_amount.toFixed(2)),
      amount: Number(taxable.toFixed(2)), // item amount after discount (taxable)
      cgst_amount: Number(cgst_amount.toFixed(2)),
      sgst_amount: Number(sgst_amount.toFixed(2)),
      igst_amount: Number(igst_amount.toFixed(2)),
      total_amount: Number(total_amount.toFixed(2)),
    };
  };

  return (
    <>
      <Modal
        isOpen={addModalOpen}
        onRequestClose={() => setAddModalOpen(false)}
        style={{
          overlay: { backgroundColor: "rgba(0,0,0,0.45)", zIndex: 9999 },
          content: {
            width: "90%",
            height: "85%",
            margin: "auto",
            borderRadius: "10px",
            padding: 0,
            inset: "60px",
            overflow: "hidden",
          },
        }}
      >
        <div className="flex flex-col h-full">
          <div className="sticky top-0 bg-white-A700 border-b px-4 py-3 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">
              Purchase Invoice Booking
            </h2>
            <button
              onClick={() => {
                setAddModalOpen(false);
                navigate("/purchase-invoice-booking");
              }}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-gray-700"
            >
              <IoClose size={22} className="text-red-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <ToastContainer />
            <Formik
              initialValues={{
                supplier_id: "",
                to_gl_account: "",
                payment_term_code: "",
                fin_tax_master_tds_id: "",
                fin_doc_type_id: "",
                fin_company_purchase_order_invoice_category_id: "",
                invoice_number: "",
                invoice_date: "",
                posting_date: new Date().toISOString().split("T")[0],
                due_date: "",
                remarks: "",
                total_item_amount: 0,
                total_tax_amount: 0,
                total_invoice_amount: 0,
                items: [
                  {
                    uuid: genUUID(),
                    item_detail_id: "",
                    item_name: "",
                    quantity: 1,
                    cost_price: 0,
                    discount_percentage: 0, // user enters %
                    discount_amount: 0,
                    amount: 0, // taxable (qty*cost - discount)
                    cgst_percent: 0,
                    sgst_percent: 0,
                    igst_percent: 0,
                    cgst_amount: 0,
                    sgst_amount: 0,
                    igst_amount: 0,
                    total_amount: 0,
                  },
                ],
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                const payload = {
                  supplier_id: values.supplier_id,
                  to_gl_account: values.to_gl_account,
                  invoice_number: values.invoice_number,
                  invoice_date: values.invoice_date,
                  payment_term_code: values.payment_term_code,
                  fin_tax_master_tds_id: values.fin_tax_master_tds_id,
                  remarks: values.remarks,
                  items: values.items.map((i) => ({
                    item_detail_id: i.item_detail_id,
                    quantity: i.quantity,
                    cost_price: i.cost_price,
                    discount_percentage: i.discount_percentage,
                  })),
                };
                console.log(payload);

                try {
                  setSubmitting(true);

                  const result = await createManualInvoice(payload);

                  if (result?.status) {
                    resetForm();
                  }
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {/* {({ values, setFieldValue, errors, touched, formik }) => { */}
              {(formik) => {
                const { values, errors, touched, setFieldValue } = formik;
                // helper: update item by uuid (keeps identity stable)
                const updateItemByUUID = (uuid, patch = {}) => {
                  const idx = values.items.findIndex((x) => x.uuid === uuid);
                  if (idx === -1) return;
                  const current = values.items[idx];
                  const updated = { ...current, ...patch };
                  const calc = computeItemValues(updated);
                  const merged = { ...updated, ...calc };
                  const newItems = values.items.map((it, i) =>
                    i === idx ? merged : it
                  );
                  setFieldValue("items", newItems);
                };

                // compute totals (derived on render)
                const totals = values.items.reduce(
                  (acc, it) => {
                    acc.total_item_amount += Number(it.amount) || 0; // taxable sum
                    acc.total_tax_amount +=
                      (Number(it.cgst_amount) || 0) +
                      (Number(it.sgst_amount) || 0) +
                      (Number(it.igst_amount) || 0);
                    acc.total_invoice_amount += Number(it.total_amount) || 0; // taxable + taxes
                    return acc;
                  },
                  {
                    total_item_amount: 0,
                    total_tax_amount: 0,
                    total_invoice_amount: 0,
                  }
                );

                const tdsBase = totals.total_item_amount; // TDS on item amount (taxable) only
                const tdsAmount = +(
                  (tdsBase * (tdsPercent || 0)) /
                  100
                ).toFixed(2);
                const totalPayable = +(
                  totals.total_invoice_amount - tdsAmount
                ).toFixed(2);

                const getRowIndex = (item) =>
                  values.items.findIndex(
                    (i) => i.item_detail_id === item.item_detail_id
                  );

                return (
                  <Form className="space-y-6">
                    {/* HEADER */}
                    <div className="grid grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium">
                          Select Supplier{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <Select
                          name="supplier_id"
                          value={supplierOptions.find(
                            (option) => option.value === values.supplier_id
                          )}
                          options={supplierOptions}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              "supplier_id",
                              selectedOption?.value || ""
                            );
                            setFieldValue(
                              "payment_term_code",
                              selectedOption?.fin_payment_term_code || ""
                            );
                            setFieldValue(
                              "fin_tax_master_tds_id",
                              selectedOption?.fin_tax_master_tds_id || ""
                            );
                            setSelectedSupplierId(
                              selectedOption?.value || null
                            );
                            if (selectedOption?.fin_tax_master_tds_id) {
                              getTaxMasterDetails(
                                selectedOption.fin_tax_master_tds_id
                              );
                            } else {
                              setTdsPercent(0);
                            }
                          }}
                          onInputChange={(inputValue) =>
                            setSearchInput(inputValue)
                          }
                          placeholder="Search Supplier"
                          isSearchable
                          className="text-sm"
                        />
                        <ErrorMessage
                          name="supplier_id"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          Payment Term
                        </label>
                        <Field
                          name="payment_term_code"
                          placeholder="Payment Term"
                          value={values.payment_term_code}
                          type="text"
                          disabled
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          Document Type
                        </label>
                        <Field
                          name="fin_doc_type_id"
                          value="Purchase Invoice"
                          type="text"
                          disabled
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          Category
                        </label>
                        <Field
                          name="fin_company_purchase_order_invoice_category_id"
                          type="text"
                          value="Non-GRN"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          Invoice No. <span className="text-red-500">*</span>
                        </label>
                        <Field
                          name="invoice_number"
                          placeholder="Invoice Number"
                          type="text"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                        <ErrorMessage
                          name="invoice_number"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          Invoice Date <span className="text-red-500">*</span>
                        </label>
                        <Field
                          name="invoice_date"
                          type="date"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                        <ErrorMessage
                          name="invoice_date"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          Posting Date
                        </label>
                        <Field
                          name="posting_date"
                          type="date"
                          readOnly
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          Remarks
                        </label>
                        <Field
                          name="remarks"
                          placeholder="Remarks"
                          as="textarea"
                          rows="1"
                          className="w-full border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium">
                          To GL Account <span className="text-red-500">*</span>
                        </label>
                        <Select
                          name="to_gl_account"
                          value={toGLAccount.find(
                            (option) => option.value === values.to_gl_account
                          )}
                          options={toGLAccount}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              "to_gl_account",
                              selectedOption?.value || ""
                            );
                          }}
                          // onInputChange={(inputValue) =>
                          //   setSearchInput(inputValue)
                          // }
                          placeholder="Search GL Account"
                          isSearchable
                          className="text-sm"
                        />
                        <ErrorMessage
                          name="to_gl_account"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">TDS</label>
                        <Select
                          name="fin_tax_master_tds_id"
                          value={allTaxMasters.find(
                            (option) =>
                              option.value === values.fin_tax_master_tds_id
                          )}
                          options={allTaxMasters}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              "fin_tax_master_tds_id",
                              selectedOption?.value || ""
                            );
                            if (selectedOption) {
                              getTaxMasterDetails(selectedOption.value);
                            } else {
                              setTdsPercent(0);
                            }
                          }}
                          // onInputChange={(inputValue) =>
                          //   setSearchInput(inputValue)
                          // }
                          placeholder="Select Tax Master"
                          isSearchable
                          className="text-sm"
                        />
                        <ErrorMessage
                          name="fin_tax_master_tds_id"
                          component="div"
                          className="text-red-500 text-xs"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block text-sm font-medium">
                          TDS (%)
                        </label>
                        <Field
                          name="tds_percent"
                          type="number"
                          value={tdsPercent}
                          readOnly
                          className="w-full border rounded px-2 py-1 text-right text-sm"
                        />

                        <label className="block text-sm font-medium">
                          TDS Amount
                        </label>
                        <input
                          className="w-full border rounded px-2 py-1 text-right text-sm"
                          type="text"
                          readOnly
                          value={tdsAmount.toFixed(2)}
                        />
                      </div>
                    </div>

                    {/* ITEMS */}
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <h3 className="font-semibold">Invoice Items</h3>
                        <div className="text-sm text-gray-600">
                          Search items will use selected supplier
                        </div>
                      </div>

                      <FieldArray name="items">
                        {({ push, remove }) => (
                          <>
                            <table className="w-full border text-sm">
                              <thead className="bg-gray-100 text-xs">
                                <tr>
                                  <th className="border p-2">Item</th>
                                  <th className="border p-2">Qty</th>
                                  <th className="border p-2">Cost Price</th>
                                  <th className="border p-2">Item Amt</th>
                                  <th className="border p-2">Discount %</th>
                                  <th className="border p-2">Discount Amt</th>
                                  <th className="border p-2">CGST %</th>
                                  <th className="border p-2">SGST %</th>
                                  <th className="border p-2">IGST %</th>
                                  <th className="border p-2">CGST Amt</th>
                                  <th className="border p-2">SGST Amt</th>
                                  <th className="border p-2">IGST Amt</th>
                                  <th className="border p-2">Total</th>
                                  <th className="border p-2">Action</th>
                                </tr>
                              </thead>

                              <tbody className="text-xs">
                                {values.items.map((item) => {
                                  const unique = item.uuid;
                                  return (
                                    <tr key={unique}>
                                      <td className="border p-1 w-60">
                                        <Select
                                          options={(itemOptions || []).map(
                                            (i) => ({
                                              label: i.itemname,
                                              value: i.item_detail_id,
                                              cost_price: i.cost_price,
                                              cgst_percent:
                                                i.cgst_percentage ??
                                                i.cgst_percent ??
                                                0,
                                              sgst_percent:
                                                i.sgst_percentage ??
                                                i.sgst_percent ??
                                                0,
                                              igst_percent:
                                                i.igst_percentage ??
                                                i.igst_percent ??
                                                0,
                                            })
                                          )}
                                          placeholder="Select Item"
                                          value={
                                            item.item_detail_id
                                              ? itemOptions
                                                  .map((i) => ({
                                                    label: i.itemname,
                                                    value: i.item_detail_id,
                                                    cost_price: i.cost_price,
                                                    cgst_percent:
                                                      i.cgst_percentage ??
                                                      i.cgst_percent ??
                                                      0,
                                                    sgst_percent:
                                                      i.sgst_percentage ??
                                                      i.sgst_percent ??
                                                      0,
                                                    igst_percent:
                                                      i.igst_percentage ??
                                                      i.igst_percent ??
                                                      0,
                                                  }))
                                                  .find(
                                                    (o) =>
                                                      o.value ===
                                                      item.item_detail_id
                                                  )
                                              : null
                                          }
                                          onChange={(selected) => {
                                            if (!selected) return;
                                            // update with item defaults and compute derived
                                            updateItemByUUID(unique, {
                                              item_detail_id: selected.value,
                                              item_name: selected.label,
                                              cost_price:
                                                Number(selected.cost_price) ||
                                                0,
                                              cgst_percent:
                                                Number(selected.cgst_percent) ||
                                                0,
                                              sgst_percent:
                                                Number(selected.sgst_percent) ||
                                                0,
                                              igst_percent:
                                                Number(selected.igst_percent) ||
                                                0,
                                            });
                                          }}
                                        />
                                        {(() => {
                                          const rowIndex = getRowIndex(item);

                                          if (
                                            rowIndex !== -1 &&
                                            errors.items?.[rowIndex]
                                              ?.item_name &&
                                            touched.items?.[rowIndex]?.item_name
                                          ) {
                                            return (
                                              <div className="text-red-500 text-xs">
                                                {
                                                  errors.items[rowIndex]
                                                    .item_name
                                                }
                                              </div>
                                            );
                                          }
                                          return null;
                                        })()}
                                      </td>

                                      <td className="border p-1">
                                        <input
                                          type="number"
                                          className="w-20 border rounded text-xs text-right"
                                          min="0"
                                          value={item.quantity}
                                          onChange={(e) =>
                                            updateItemByUUID(unique, {
                                              quantity:
                                                Number(e.target.value) || 0,
                                            })
                                          }
                                        />
                                        {(() => {
                                          const rowIndex = getRowIndex(item);

                                          if (
                                            rowIndex !== -1 &&
                                            errors.items?.[rowIndex]
                                              ?.quantity &&
                                            touched.items?.[rowIndex]?.quantity
                                          ) {
                                            return (
                                              <div className="text-red-500 text-xs">
                                                {
                                                  errors.items[rowIndex]
                                                    .quantity
                                                }
                                              </div>
                                            );
                                          }
                                          return null;
                                        })()}
                                      </td>

                                      <td className="border p-1 text-right">
                                        <input
                                          type="number"
                                          className="w-28 border rounded text-xs text-right"
                                          // min="0"
                                          value={item.cost_price}
                                          onChange={(e) =>
                                            updateItemByUUID(unique, {
                                              cost_price:
                                                Number(e.target.value) || 0,
                                            })
                                          }
                                        />
                                        {(() => {
                                          const rowIndex = getRowIndex(item);

                                          if (
                                            rowIndex !== -1 &&
                                            errors.items?.[rowIndex]
                                              ?.cost_price &&
                                            touched.items?.[rowIndex]
                                              ?.cost_price
                                          ) {
                                            return (
                                              <div className="text-red-500 text-xs">
                                                {
                                                  errors.items[rowIndex]
                                                    .cost_price
                                                }
                                              </div>
                                            );
                                          }
                                          return null;
                                        })()}
                                      </td>

                                      <td className="border p-1 text-right">
                                        {(Number(item.amount) || 0).toFixed(2)}
                                      </td>

                                      <td className="border p-1">
                                        <input
                                          type="number"
                                          className="w-20 border rounded text-xs text-right"
                                          min="0"
                                          max="100"
                                          value={item.discount_percentage}
                                          onChange={(e) =>
                                            updateItemByUUID(unique, {
                                              discount_percentage:
                                                Number(e.target.value) || 0,
                                            })
                                          }
                                        />
                                      </td>

                                      <td className="border p-1 text-right">
                                        {(
                                          Number(item.discount_amount) || 0
                                        ).toFixed(2)}
                                      </td>

                                      <td className="border p-1 text-right">
                                        {Number(item.cgst_percent || 0).toFixed(
                                          2
                                        )}
                                      </td>
                                      <td className="border p-1 text-right">
                                        {Number(item.sgst_percent || 0).toFixed(
                                          2
                                        )}
                                      </td>
                                      <td className="border p-1 text-right">
                                        {Number(item.igst_percent || 0).toFixed(
                                          2
                                        )}
                                      </td>

                                      <td className="border p-1 text-right">
                                        {(
                                          Number(item.cgst_amount) || 0
                                        ).toFixed(2)}
                                      </td>
                                      <td className="border p-1 text-right">
                                        {(
                                          Number(item.sgst_amount) || 0
                                        ).toFixed(2)}
                                      </td>
                                      <td className="border p-1 text-right">
                                        {(
                                          Number(item.igst_amount) || 0
                                        ).toFixed(2)}
                                      </td>

                                      <td className="border p-1 text-right font-semibold">
                                        {(
                                          Number(item.total_amount) || 0
                                        ).toFixed(2)}
                                      </td>

                                      <td className="border p-1 text-center">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const idx = values.items.findIndex(
                                              (x) => x.uuid === unique
                                            );
                                            if (idx !== -1) remove(idx);
                                          }}
                                          className="text-red-600 hover:underline"
                                        >
                                          <FiTrash2 size={16} />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>

                            <div className="mt-2">
                              <button
                                type="button"
                                onClick={() =>
                                  push({
                                    uuid: genUUID(),
                                    item_detail_id: "",
                                    item_name: "",
                                    quantity: 1,
                                    cost_price: 0,
                                    discount_percentage: 0,
                                    discount_amount: 0,
                                    amount: 0,
                                    cgst_percent: 0,
                                    sgst_percent: 0,
                                    igst_percent: 0,
                                    cgst_amount: 0,
                                    sgst_amount: 0,
                                    igst_amount: 0,
                                    total_amount: 0,
                                  })
                                }
                                className="bg-blue-600 text-xs text-white-A700 px-4 py-1 rounded"
                              >
                                + Add Item
                              </button>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </div>

                    {/* TOTALS */}
                    <div className="grid grid-cols-3 gap-4 px-4 py-1 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium">
                          Total Item Amount
                        </label>
                        <input
                          readOnly
                          className="w-full border rounded px-2 py-1 text-right text-sm"
                          value={totals.total_item_amount.toFixed(2)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          Total Tax Amount
                        </label>
                        <input
                          readOnly
                          className="w-full border rounded px-2 py-1 text-right text-sm"
                          value={totals.total_tax_amount.toFixed(2)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          Total Invoice Amount (incl. tax)
                        </label>
                        <input
                          readOnly
                          className="w-full border rounded px-2 py-1 text-right font-semibold text-sm"
                          value={totals.total_invoice_amount.toFixed(2)}
                        />
                      </div>
                    </div>

                    {/* TDS & Payable */}
                    <div className="grid grid-cols-3 gap-4 px-4 py-1 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium">
                          Total Payable (incl. tax - TDS)
                        </label>
                        <input
                          readOnly
                          className="w-full border rounded px-2 py-1 text-right text-sm font-semibold"
                          value={totalPayable.toFixed(2)}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="bg-green-600 text-white-A700 text-sm px-5 py-2 rounded hover:bg-green-700"
                      >
                        Save Invoice
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </Modal>
    </>
  );
}
